import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { showModal, installModalFallback } from "./modalHelper";
import ProjetFormModal, { PROJET_MODAL_ID } from "./ProjetFormModal";
import useReferentiels from "./useReferentiels";
import {
    API, ROUTES, TYPES, STATUTS, findType, findStatut, unwrap, apiMessage, toast, formatDate, formatAmount,
    TypeBadge, StatutBadge, PrioriteIcon, ProgressBar,
} from "./projetsCommon";
import "./GestionProjets.css";

const ProjetsList = () => {
    const [projets, setProjets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [openKey, setOpenKey] = useState(0);
    const { clients, employes } = useReferentiels();

    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [statutFilter, setStatutFilter] = useState("ouverts");   // "ouverts" = hors terminés / annulés
    const [respFilter, setRespFilter] = useState("");
    const [retardOnly, setRetardOnly] = useState(false);

    useEffect(() => { fetchProjets(); }, []);
    useEffect(() => installModalFallback(), []);

    const fetchProjets = () => {
        axiosInstance.get(API.projets)
            .then((res) => setProjets(unwrap(res) || []))
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les projets")))
            .finally(() => setLoading(false));
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return projets.filter((p) => {
            const statut = findStatut(p.statut).value;
            if (statutFilter === "ouverts" && (statut === 2 || statut === 6)) return false;
            if (statutFilter && statutFilter !== "ouverts" && statut !== Number(statutFilter)) return false;
            if (typeFilter && findType(p.type).value !== Number(typeFilter)) return false;
            if (respFilter && String(p.responsableId) !== respFilter) return false;
            if (retardOnly && !p.enRetard) return false;
            if (!term) return true;
            return [p.code, p.nom, p.clientName, p.responsableNom, p.lieu, p.ville]
                .filter(Boolean).join(" ").toLowerCase().includes(term);
        });
    }, [projets, search, typeFilter, statutFilter, respFilter, retardOnly]);

    const openCreate = () => { setEditing(null); setOpenKey((k) => k + 1); showModal(PROJET_MODAL_ID); };
    const openEdit = (p) => { setEditing(p); setOpenKey((k) => k + 1); showModal(PROJET_MODAL_ID); };

    const handleDelete = (p) => {
        Swal.fire({
            title: `Supprimer le projet « ${p.nom} » ?`,
            text: p.nbDocuments ? "Ce projet a des documents : retirez-les d'abord." : "Cette action est définitive.",
            icon: "warning", showCancelButton: true, confirmButtonText: "Oui, supprimer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try {
                await axiosInstance.delete(API.projet(p.id));
                toast("success", "Projet supprimé");
                fetchProjets();
            } catch (error) {
                Swal.fire({ icon: "error", title: "Suppression impossible", text: apiMessage(error, "Opération échouée") });
            }
        });
    };

    return (
        <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap my-3">
                <h3 className="mb-2">Projets</h3>
                <button className="btn btn-primary btn-ajouter mb-2" onClick={openCreate}>
                    <i className="fas fa-plus"></i> Nouveau projet
                </button>
            </div>

            <div className="form-row align-items-end mb-3">
                <div className="col-md-3 mb-2">
                    <input className="form-control" placeholder="Rechercher (code, nom, client…)" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="col-md-2 mb-2">
                    <select className="form-control" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                        <option value="">Tous les types</option>
                        {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div className="col-md-2 mb-2">
                    <select className="form-control" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
                        <option value="ouverts">Projets ouverts</option>
                        <option value="">Tous les statuts</option>
                        {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
                <div className="col-md-2 mb-2">
                    <select className="form-control" value={respFilter} onChange={(e) => setRespFilter(e.target.value)}>
                        <option value="">Tous les responsables</option>
                        {employes.map((e) => <option key={e.id} value={e.id}>{e.nomComplet}</option>)}
                    </select>
                </div>
                <div className="col-md-3 mb-2 d-flex align-items-center justify-content-between">
                    <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="retardOnly" checked={retardOnly} onChange={(e) => setRetardOnly(e.target.checked)} />
                        <label className="custom-control-label" htmlFor="retardOnly">En retard uniquement</label>
                    </div>
                    <span className="small text-muted">{filtered.length} projet(s)</span>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover table-sm bg-white">
                    <thead className="thead-light">
                        <tr>
                            <th></th>
                            <th>Projet</th>
                            <th>Type</th>
                            <th>Client</th>
                            <th>Responsable</th>
                            <th>Échéance</th>
                            <th style={{ minWidth: 120 }}>Avancement</th>
                            <th className="text-right">Budget</th>
                            <th>Statut</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="10" className="text-center py-4">Chargement...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan="10" className="text-center text-muted py-4">Aucun projet</td></tr>
                        ) : filtered.map((p) => (
                            <tr key={p.id} className={p.enRetard ? "gp-row-late" : ""}>
                                <td><PrioriteIcon priorite={p.priorite} /></td>
                                <td>
                                    <Link to={ROUTES.fiche(p.id)}>
                                        {p.code && <span className="text-muted mr-1">{p.code}</span>}<strong>{p.nom}</strong>
                                    </Link>
                                    {(p.ville || p.lieu) && <div className="small text-muted">{p.ville || p.lieu}</div>}
                                </td>
                                <td><TypeBadge type={p.type} /></td>
                                <td>{p.clientName || "—"}</td>
                                <td>{p.responsableNom || "—"}</td>
                                <td className={`small text-nowrap ${p.enRetard ? "text-danger font-weight-bold" : ""}`}>
                                    {p.enRetard && <i className="fas fa-exclamation-triangle mr-1"></i>}{formatDate(p.dateFin)}
                                </td>
                                <td><ProgressBar value={p.avancement} enRetard={p.enRetard} /></td>
                                <td className="text-right text-nowrap">{formatAmount(p.budget)}</td>
                                <td><StatutBadge statut={p.statut} /></td>
                                <td className="text-right text-nowrap">
                                    <Link to={ROUTES.fiche(p.id)} className="btn btn-sm btn-outline-info mr-1" title="Ouvrir">
                                        <i className="fas fa-folder-open"></i>
                                    </Link>
                                    <button className="btn btn-sm btn-outline-secondary mr-1" title="Modifier" onClick={() => openEdit(p)}>
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => handleDelete(p)}>
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ProjetFormModal projet={editing} openKey={openKey} clients={clients} employes={employes} onSaved={fetchProjets} />
        </div>
    );
};

export default ProjetsList;
